"use client";

import React from "react";
import { CircleCheck as CheckCircle2, ShieldCheck, Clock } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
function SidingHeroQuoteFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    let name = "";
    let phone = "";
    let email = "";
    try {
      const formEl = e.currentTarget;
      const formData = new FormData(formEl);
      name = formData.get("name") as string || (formEl.elements.namedItem("name") as HTMLInputElement)?.value || "";
      phone = formData.get("phone") as string || (formEl.elements.namedItem("phone") as HTMLInputElement)?.value || "";
      email = formData.get("email") as string || (formEl.elements.namedItem("email") as HTMLInputElement)?.value || "";
    } catch {
      // Ignore reading errors
    }

    // Secondary non-blocking fire-and-forget call to wvc-forms endpoint
    try {
      const client = getWvcClient();
      client?.pluginRequest?.("wvc-forms/v1/forms/siding_hero_quote_form_siding/submit", {
        method: "POST",
        data: {
          values: {
            name,
            phone,
            email
          },
          name,
          phone,
          email
        }
      })?.catch?.((err: any) => {
        console.error("Secondary form submission failed:", err);
      });
    } catch (err) {
      console.error("Secondary form submission error:", err);
    }

    // Maintain existing FormProvider pipeline
    return handleSubmit(e);
  };
  if (isSubmitted && !submitError) {
    return <Alert className="border-primary/40 bg-primary/10 text-foreground">
        <CheckCircle2 className="size-4 text-primary" />
        <AlertTitle className="font-semibold text-foreground">Quote Requested</AlertTitle>
        <AlertDescription className="text-muted-foreground text-xs">
          {successMessage}
        </AlertDescription>
      </Alert>;
  }
  return <Form onSubmit={handleFormSubmit} className="space-y-4">
      {submitError && <Alert variant="destructive" className="mb-6">
          <AlertTitle className="font-semibold">Notice</AlertTitle>
          <AlertDescription className="text-xs">
            {submitError}
          </AlertDescription>
        </Alert>}

      {/* Name field */}
      <FormField control={control} name="name" rules={{
      required: "Please enter your name (at least 2 characters).",
      minLength: {
        value: 2,
        message: "Please enter your name (at least 2 characters)."
      }
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel htmlFor="siding-hero-name" className="block text-xs font-medium uppercase tracking-wider text-card-foreground font-mono">
              Your Name <span className="text-primary">*</span>
            </FormLabel>
            <FormControl>
              <Input {...field} id="siding-hero-name" type="text" autoComplete="name" placeholder="e.g. John Miller" className="h-11 rounded-sm border-border bg-background px-3 text-sm focus-visible:ring-primary" />
            </FormControl>
            <FormMessage className="text-xs text-destructive font-default mt-1" />
          </FormItem>} />

      {/* Phone field */}
      <FormField control={control} name="phone" rules={{
      required: "Phone number is required."
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel htmlFor="siding-hero-phone" className="block text-xs font-medium uppercase tracking-wider text-card-foreground font-mono">
              Phone Number <span className="text-primary">*</span>
            </FormLabel>
            <FormControl>
              <Input {...field} id="siding-hero-phone" type="tel" autoComplete="tel" placeholder="e.g. (403) 555-0192" className="h-11 rounded-sm border-border bg-background px-3 text-sm focus-visible:ring-primary" />
            </FormControl>
            <FormMessage className="text-xs text-destructive font-default mt-1" />
          </FormItem>} />

      {/* Email field */}
      <FormField control={control} name="email" rules={{
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please provide a valid email address."
      }
    }} render={({
      field
    }) => <FormItem className="space-y-1.5">
            <FormLabel htmlFor="siding-hero-email" className="block text-xs font-medium uppercase tracking-wider text-card-foreground font-mono">
              Email Address{" "}
              <span className="font-normal normal-case text-muted-foreground ml-1" style={{
          fontSize: "10px"
        }}>
                (Optional)
              </span>
            </FormLabel>
            <FormControl>
              <Input {...field} id="siding-hero-email" type="email" autoComplete="email" placeholder="e.g. john@example.com" className="h-11 rounded-sm border-border bg-background px-3 text-sm focus-visible:ring-primary" />
            </FormControl>
            <FormMessage className="text-xs text-destructive font-default mt-1" />
          </FormItem>} />

      {/* Privacy note */}
      <p className="text-[10px] text-muted-foreground leading-normal font-default">
        We use the details you submit to respond to your inquiry and prepare your quote. Read our{" "}
        <Link to="/PrivacyPolicyPage" className="underline underline-offset-2 text-foreground hover:text-primary transition-colors">
          Privacy Policy
        </Link>
        .
      </p>

      {/* Submit button */}
      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full h-11 uppercase font-bold tracking-wide rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-xs">
          {isSubmitting ? "Submitting..." : "REQUEST A FREE QUOTE"}
        </Button>
      </div>

      {/* Reassurance text */}
      <div className="flex items-center justify-center gap-4 pt-2 text-xs text-muted-foreground font-mono">
        <span className="inline-flex items-center gap-1">
          <ShieldCheck className="size-3.5 text-primary shrink-0" />
          No obligation
        </span>
        <span>•</span>
        <span className="inline-flex items-center gap-1">
          <Clock className="size-3.5 text-primary shrink-0" />
          Fast response
        </span>
      </div>
    </Form>;
}
function SidingHeroQuoteForm() {
  return <FormProvider formKey="siding_hero_quote_form_siding" formId="siding_hero_quote_form_siding" sectionName="sidinghero" defaultValues={{
    name: "",
    phone: "",
    email: ""
  }} validationRules={{
    name: {
      required: true,
      minLength: 2
    },
    phone: {
      required: true
    },
    email: {
      required: false,
      pattern: "email"
    }
  }} submitText="REQUEST A FREE QUOTE" successMessage="Thank you! We've received your request. Our team will contact you shortly." errorMessage="Submission failed. Please try again or call us directly.">
      <SidingHeroQuoteFormContent />
    </FormProvider>;
}
export default function SidingHero() {
  const shouldReduceMotion = useReducedMotion();
  const trustBadges = ["Hardie Siding", "LP SmartSide", "Lux Panels", "Professional Installation"];
  return <section id="sidinghero" data-nav="light" className="relative w-full bg-background text-foreground py-10 md:py-16 lg:py-20 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left Column (55% on desktop -> 7 cols) */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7 flex flex-col gap-6">
            {/* Technical coordinate marker */}
            <div className="flex items-center gap-3 text-xs font-mono text-muted-foreground uppercase tracking-wider">
              <span className="flex items-center gap-1 text-primary">
              </span>
            </div>

            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2">
              <span className="text-xs font-mono font-semibold uppercase tracking-widest text-primary bg-primary/10 px-2.5 py-1 rounded-sm">
                Siding &amp; Cladding • Calgary
              </span>
            </div>

            {/* Headline (H1) */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground uppercase">
              Siding supply &amp; installation in Calgary
            </h1>

            {/* Supporting line */}
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-default">
              Premium siding and cladding solutions supplied and professionally installed for Calgary homes.
            </p>

            {/* Trust line badges */}
            <div className="flex flex-wrap items-center gap-2.5 pt-1">
              {trustBadges.map((badge, i) => <div key={badge} data-index={i} className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-foreground bg-muted/60 border border-border px-3 py-1.5 rounded-sm">
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                  <span>{badge}</span>
                </div>)}
            </div>

            {/* Featured Image */}
            <div className="relative mt-3 overflow-hidden rounded-md border border-border bg-muted aspect-16/10 shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/james_hardie_siding.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/james_hardie_siding.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/james_hardie_siding.webp-wvc-sizes" alt="James Hardie siding installation in Calgary" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </motion.div>

          {/* Right Column (45% on desktop -> 5 cols) */}
          <motion.div initial={shouldReduceMotion ? {} : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5">
            <div className="bg-card text-card-foreground border border-border rounded-md shadow-md p-6 sm:p-8">
              {/* Form header */}
              <div className="border-b border-border pb-5 mb-6">
                <span className="text-xs font-mono uppercase tracking-widest text-primary font-semibold block mb-1">
                  Free Estimate
                </span>
                <h2 className="text-xl sm:text-2xl font-default font-bold text-card-foreground tracking-tight uppercase">
                  Get a free siding quote
                </h2>
                <p className="text-sm text-muted-foreground mt-1.5 leading-normal font-default">
                  Get a free estimate for your siding project. We'll get back to you shortly.
                </p>
              </div>

              <SidingHeroQuoteForm />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}